/**
 * Maps Drake checklist categories to TaxDocument types
 * Used for matching uploaded documents to document requirements
 */

// Drake checklist category to TaxDocument type mapping
export const drakeCategoryToDocumentType: Record<string, string[]> = {
  // Wage income
  'W-2': ['w2'],
  'W2': ['w2'],
  'Wages': ['w2'],

  // Self-employment / contract income
  '1099-NEC': ['1099_nec'],
  '1099-MISC': ['1099_misc'],
  '1099NEC': ['1099_nec'],
  '1099MISC': ['1099_misc'],
  'Self-Employment': ['1099_nec', '1099_misc', 'business_expenses'],
  'Contract Income': ['1099_nec', '1099_misc'],

  // Investment income
  '1099-INT': ['1099_int'],
  '1099-DIV': ['1099_div'],
  '1099-B': ['1099_b'],
  '1099INT': ['1099_int'],
  '1099DIV': ['1099_div'],
  '1099B': ['1099_b'],
  'Interest Income': ['1099_int'],
  'Dividend Income': ['1099_div'],
  'Investment Sales': ['1099_b'],
  'Capital Gains': ['1099_b'],

  // Retirement income
  '1099-R': ['1099_r'],
  '1099R': ['1099_r'],
  'Retirement': ['1099_r'],
  'Pension': ['1099_r'],
  'IRA Distribution': ['1099_r'],

  // Schedule K-1
  'K-1': ['k1'],
  'K1': ['k1'],
  'Schedule K-1': ['k1'],
  'Partnership': ['k1'],
  'S-Corp': ['k1'],

  // Deductions
  '1098': ['1098'],
  '1098-T': ['1098_t'],
  '1098T': ['1098_t'],
  'Mortgage Interest': ['1098'],
  'Tuition': ['1098_t'],
  'Education': ['1098_t'],

  // Property
  'Property Tax': ['property_tax'],
  'Real Estate Tax': ['property_tax'],

  // Charitable
  'Charitable': ['charitable_donations'],
  'Donations': ['charitable_donations'],
  'Charitable Contributions': ['charitable_donations'],

  // Medical
  'Medical': ['medical_expenses'],
  'Healthcare': ['medical_expenses'],
  'Medical Expenses': ['medical_expenses'],

  // Business
  'Business Expenses': ['business_expenses'],
  'Business': ['business_expenses'],

  // Prior year
  'Prior Year': ['prior_year_return'],
  'Prior Return': ['prior_year_return'],

  // ID
  'ID': ['id_verification'],
  'Identification': ['id_verification'],
  'Driver License': ['id_verification'],
};

// TaxDocument type display names (copy from types/index.ts for convenience)
export const documentTypeDisplayNames: Record<string, string> = {
  w2: 'W-2',
  '1099_misc': '1099-MISC',
  '1099_nec': '1099-NEC',
  '1099_int': '1099-INT',
  '1099_div': '1099-DIV',
  '1099_b': '1099-B',
  '1099_r': '1099-R',
  k1: 'K-1',
  '1098': '1098 Mortgage Interest',
  '1098_t': '1098-T Tuition',
  property_tax: 'Property Tax Statement',
  charitable_donations: 'Charitable Donations',
  medical_expenses: 'Medical Expenses',
  business_expenses: 'Business Expenses',
  prior_year_return: 'Prior Year Return',
  id_verification: 'ID Verification',
  unsigned_8879: 'Unsigned 8879',
  signed_8879: 'Signed 8879',
  engagement_letter: 'Engagement Letter',
  completed_return: 'Completed Tax Return',
  other: 'Other Document',
};

// Category badge colors based on type
export const categoryColors: Record<string, string> = {
  // Income - blue shades
  'W-2': '#0078d4',
  'W2': '#0078d4',
  'Wages': '#0078d4',
  '1099-NEC': '#2b88d8',
  '1099-MISC': '#2b88d8',
  '1099-INT': '#005a9e',
  '1099-DIV': '#005a9e',
  '1099-B': '#005a9e',
  '1099-R': '#004578',
  'K-1': '#004578',

  // Deductions - green shades
  '1098': '#107c10',
  '1098-T': '#107c10',
  'Property Tax': '#0b6a0b',
  'Charitable': '#0b6a0b',
  'Medical': '#0b6a0b',
  'Business Expenses': '#0b6a0b',

  // Other - gray/purple
  'Prior Year': '#8764b8',
  'ID': 'var(--tl-text-secondary)',
  'Other': 'var(--tl-text-secondary)',
};

/**
 * Get matching document types for a Drake checklist category
 */
export function getDocumentTypesForCategory(category: string): string[] {
  // Direct match
  if (drakeCategoryToDocumentType[category]) {
    return drakeCategoryToDocumentType[category];
  }

  // Case-insensitive search
  const lowerCategory = category.toLowerCase();
  for (const [key, types] of Object.entries(drakeCategoryToDocumentType)) {
    if (key.toLowerCase() === lowerCategory) {
      return types;
    }
  }

  // Partial match
  for (const [key, types] of Object.entries(drakeCategoryToDocumentType)) {
    if (lowerCategory.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerCategory)) {
      return types;
    }
  }

  return ['other'];
}

/**
 * Get a category color for display
 */
export function getCategoryColor(category: string): string {
  // Direct match
  if (categoryColors[category]) {
    return categoryColors[category];
  }

  // Case-insensitive search
  for (const [key, color] of Object.entries(categoryColors)) {
    if (category.toLowerCase().includes(key.toLowerCase())) {
      return color;
    }
  }

  return 'var(--tl-text-secondary)'; // Default gray
}

/**
 * Check if a document type matches a Drake checklist item
 */
export function documentMatchesChecklistItem(
  documentType: string,
  checklistCategory: string,
  checklistDocumentType?: string
): boolean {
  const matchingTypes = getDocumentTypesForCategory(checklistCategory);
  if (matchingTypes.includes(documentType)) {
    return true;
  }

  // Also check if the checklist item's document type matches
  if (checklistDocumentType) {
    const checklistTypes = getDocumentTypesForCategory(checklistDocumentType);
    if (checklistTypes.includes(documentType)) {
      return true;
    }
  }

  return false;
}

/**
 * Format currency amount
 */
export function formatCurrency(amount?: number): string {
  if (amount === undefined || amount === null) {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
